import {CircleCheckBig, CircleX, Loader} from "lucide-react";
import {Mentee} from "@/types/Mentee.ts";
import {useTranslation} from "react-i18next";
import {isPreferenceSubmissionPeriod} from "@/utils/time-helper.ts";
import {Card, CardContent} from "@/components/ui/card.tsx";
import {SectionTitle, BlockTitle} from "@/components/ui/headings";
import {Link} from "react-router-dom";

type TaskFlowProps = {
    mentee: Mentee,
    submitPreferenceDeadline: Date | undefined,
    selectMentorDeadline: Date | undefined,
}

const TaskFlow = ({mentee, submitPreferenceDeadline, selectMentorDeadline}: TaskFlowProps) => {
    const {t} = useTranslation();

    const menteeGeneralProfileKeys = ["supervisor", "institution", "studentStatus", "discipline", "region", "bio", "phoneNumber"];

    const getIncompleteFields = (profile: Mentee): string[] => {
        return menteeGeneralProfileKeys
            .filter(key => !profile[key as keyof Mentee])
            .map((key) => t(`mentee.${key}`));
    };

    const incompleteFields = getIncompleteFields(mentee);

    const tasks = [
        {
            id: "profile",
            completed: incompleteFields.length === 0,
            title: t("dashboard.completeYourProfile"),
            link: `/mentees/${mentee.id}/edit`,
            description: incompleteFields.length > 0 ? `${t("dashboard.missing")}: ${incompleteFields.join(", ")}` : undefined,
        },
        {
            id: "photo",
            completed: !!mentee.user.profilePictureSrc,
            title: t("dashboard.uploadProfilePhoto"),
            link: `/mentees/${mentee.id}`,
        },
        {
            id: "preference",
            completed: !!mentee.menteePreference,
            title: t("preference.submit"),
            link: `/mentees/${mentee.id}/preference`,
            description: submitPreferenceDeadline ? t("dashboard.deadline", {deadline: submitPreferenceDeadline.toDateString()}) : undefined,
        },
        {
            id: "mentor",
            completed: !!mentee.mentor,
            inProgress: isPreferenceSubmissionPeriod(submitPreferenceDeadline),
            title: t("dashboard.selectMentor"),
            description: selectMentorDeadline ? t("dashboard.deadline", {deadline: selectMentorDeadline.toDateString()}) : undefined,
        },
    ];

    return (
        <Card className="rounded-xl border border-border bg-bg-card shadow-sm">
            <CardContent className="p-6">
                <SectionTitle className="mb-4">{t("dashboard.yourAccount")}</SectionTitle>

                <ul className="space-y-0">
                    {tasks.map((task, index) => (
                        <li key={task.id} className="relative flex gap-4 pb-6 last:pb-0">
                            {index < tasks.length - 1 && (
                                <span
                                    className="absolute left-[14px] top-8 bottom-0 w-px bg-border"
                                    aria-hidden
                                />
                            )}
                            <span className="relative z-10 flex h-7 w-7 shrink-0 items-center justify-center">
                                {task.inProgress ? (
                                    <Loader className="h-5 w-5 animate-spin text-text-3" />
                                ) : task.completed ? (
                                    <CircleCheckBig className="h-6 w-6 text-status-success" />
                                ) : (
                                    <CircleX className="h-6 w-6 text-status-danger" />
                                )}
                            </span>
                            <div className="min-w-0 flex-1">
                                <BlockTitle className="text-base font-medium">
                                    {task.link ? (
                                        <Link to={task.link} className="text-text-1 hover:text-springGreen transition-colors">
                                            {task.title}
                                        </Link>
                                    ) : (
                                        task.title
                                    )}
                                </BlockTitle>
                                {task.description && (
                                    <p className="mt-1 text-sm text-text-2">
                                        {task.description}
                                    </p>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            </CardContent>
        </Card>
    );
};

export default TaskFlow;
